import React from "react";
import { Info, ArrowRight, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Optional `delta` prop = { pct: number|null, label?: string, invert?: boolean }.
 * - `pct` = percent change vs a prior period (may be null when prior is 0)
 * - `invert` = true for expenses/fees where a DECREASE is good (green)
 *
 * Optional `tone` prop = "forest" | "navy" | "ochre" | "plum" | "rose" | "teal"
 * — solid dark background palette so multiple cards can sit side-by-side
 * without feeling monotonous. Delta pills stay legible on every tone
 * because the "good" pill uses white/15 and the "bad" pill uses ochre.
 */
const TONE_BG = {
  forest: "bg-forest",
  navy:   "bg-[#1E3A5F]",
  ochre:  "bg-[#8A5A1E]",
  plum:   "bg-[#4A2B57]",
  rose:   "bg-[#7A2E3F]",
  teal:   "bg-[#1E5A5A]",
};

export default function MetricCard({ label, value, sub, accent, testid, icon: Icon, tip, tipAction, delta, tone = "forest" }) {
  const bg = TONE_BG[tone] || TONE_BG.forest;
  return (
    <div
      className={`p-4 2xl:p-5 relative min-w-0 rounded-lg border border-black ${bg} text-white`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-3 gap-2">
        <div className="overline flex items-center gap-1.5 min-w-0 !text-white/80">
          <span className="truncate">{label}</span>
          {tip && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`What does ${label} mean?`}
                    data-testid={testid ? `${testid}-tip` : "metric-tip"}
                    className="text-white/60 hover:text-white transition-colors p-0.5 -m-0.5 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                  >
                    <Info className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="start"
                  className="max-w-[300px] bg-ink text-paper p-3 text-[11px] leading-snug normal-case tracking-normal font-normal whitespace-normal"
                >
                  {tip}
                  {tipAction && (
                    <Link
                      to={tipAction.to}
                      data-testid={testid ? `${testid}-tip-cta` : "metric-tip-cta"}
                      className="mt-2.5 pt-2.5 border-t border-paper/15 flex items-center gap-1.5 text-paper hover:text-forest-50 font-medium"
                    >
                      <span>{tipAction.label}</span>
                      <ArrowRight className="w-3 h-3" strokeWidth={2} />
                    </Link>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {Icon && <Icon className="w-4 h-4 text-white/70" strokeWidth={1.5} />}
      </div>
      <div className="num text-2xl 2xl:text-3xl font-medium tracking-tight leading-tight break-words text-white">
        {value}
      </div>
      {sub && <div className="text-xs text-white/70 mt-1.5 break-words">{sub}</div>}
      {delta && delta.pct != null && (
        <Delta pct={delta.pct} label={delta.label} invert={delta.invert} testid={testid} />
      )}
    </div>
  );
}

function Delta({ pct, label, invert, testid }) {
  const positive = pct > 0;
  const zero = pct === 0;
  // For expenses/fees, "positive" (higher) is BAD, so we invert the color.
  const goodDirection = invert ? !positive : positive;
  const cls = zero
    ? "bg-white/10 text-white/70"
    : goodDirection
      ? "bg-white/15 text-white"
      : "bg-ochre/25 text-ochre-50";
  const Icon = zero ? Minus : positive ? TrendingUp : TrendingDown;
  const sign = pct > 0 ? "+" : "";
  return (
    <div
      className={`mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${cls}`}
      data-testid={testid ? `${testid}-delta` : "metric-delta"}
    >
      <Icon className="w-3 h-3" strokeWidth={2.5} />
      <span className="num">{sign}{pct}%</span>
      {label && <span className="text-white/70 font-normal ml-0.5">{label}</span>}
    </div>
  );
}
